import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { publicApi, api } from "../lib/api";
import { toast } from "../hooks/use-toast";
import SearchableSelect from "../components/SearchableSelect";
import { ArrowRight, ArrowLeft, CheckCircle2, X, Check, Upload, Trash2, FileText, Loader2 } from "lucide-react";

const STEPS = ["You", "Expertise", "Motivation", "Review"];

const FieldError = ({ msg }) => msg ? <div className="text-[12px] text-[#7a1f13] mt-1">{msg}</div> : null;

const wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

const PROFILE_OPTIONS = [
  { key: "linkedin", label: "LinkedIn profile" },
  { key: "website",  label: "Professional website or portfolio" },
  { key: "cv",       label: "CV" },
  { key: "other",    label: "Other professional webpage", helper: "e.g. company/team profile, university profile, professional association profile, speaker bio, published work or industry profile" },
];

const CV_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const CV_MAX_BYTES = 10 * 1024 * 1024;

const Join = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState({ skills: [], industries: [], hear_sources: [], countries: [] });
  const [errors, setErrors] = useState({});
  const [cvUploading, setCvUploading] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", country: "",
    hear: "", hear_other: "", hear_referrer: "",
    role: "", years: "", industry: "", industry_other: "", skills: [], skills_other: "",
    profile_kind: "", profile_url: "", cv_storage_path: "", cv_filename: "",
    motivation: "", commitment_agreed: false, terms_agreed: false,
  });

  useEffect(() => { publicApi.get("/options").then(({ data }) => setOptions(data)).catch(() => {}); }, []);

  const upd = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const toggleSkill = (s) => setForm(f => ({ ...f, skills: f.skills.includes(s) ? f.skills.filter(x => x !== s) : [...f.skills, s] }));

  const pickProfileKind = (kind) => {
    setForm(f => ({
      ...f,
      profile_kind: kind,
      // Reset the other fields when switching selection so no stale data leaks
      profile_url: ["linkedin", "website", "other"].includes(kind) ? f.profile_url : "",
      cv_storage_path: kind === "cv" ? f.cv_storage_path : "",
      cv_filename: kind === "cv" ? f.cv_filename : "",
    }));
    setErrors(e => ({ ...e, profile_url: undefined, cv_upload: undefined }));
  };

  const uploadCv = async (file) => {
    if (!file) return;
    if (file.size > CV_MAX_BYTES) {
      setErrors(e => ({ ...e, cv_upload: "CV must be 10 MB or smaller" }));
      return;
    }
    const allowed = [".pdf", ".doc", ".docx"];
    const nameLower = (file.name || "").toLowerCase();
    if (!allowed.some(ext => nameLower.endsWith(ext))) {
      setErrors(e => ({ ...e, cv_upload: "Please upload a PDF, DOC or DOCX file" }));
      return;
    }
    setCvUploading(true);
    setErrors(e => ({ ...e, cv_upload: undefined }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/applications/cv-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, cv_storage_path: data.storage_path, cv_filename: data.filename }));
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Please try again.";
      setErrors(e => ({ ...e, cv_upload: typeof detail === "string" ? detail : "Upload failed" }));
    } finally {
      setCvUploading(false);
    }
  };

  const clearCv = () => setForm(f => ({ ...f, cv_storage_path: "", cv_filename: "" }));

  const validate = (idx) => {
    const e = {};
    if (idx === 0) {
      if (!form.first_name.trim()) e.first_name = "Required";
      if (!form.last_name.trim()) e.last_name = "Required";
      if (!form.email.trim()) e.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
      if (!form.country) e.country = "Please select your country";
      if (!form.hear) e.hear = "Please choose an option";
      if (form.hear === "Other" && !form.hear_other.trim()) e.hear_other = "Please specify";
    }
    if (idx === 1) {
      if (!form.role.trim()) e.role = "Required";
      if (!form.years) e.years = "Required";
      else if (!/^\d{1,3}$/.test(form.years)) e.years = "Whole numbers only, up to 3 digits";
      if (!form.industry) e.industry = "Please select your industry";
      if (form.industry === "Other" && !form.industry_other.trim()) e.industry_other = "Please specify";
      if (form.skills.length === 0) e.skills = "Select at least one";
      if (form.skills.includes("Other") && !form.skills_other.trim()) e.skills_other = "Please specify";
      // Professional profile is optional overall, but if the applicant picked
      // linkedin/website/other, the URL must be plausible.
      if (["linkedin", "website", "other"].includes(form.profile_kind) && form.profile_url.trim()) {
        if (!/^https?:\/\/.+/.test(form.profile_url.trim())) e.profile_url = "Please include the full URL, starting with http:// or https://";
      }
    }
    if (idx === 2) {
      const wc = wordCount(form.motivation);
      if (wc < 20) e.motivation = `Please write at least 20 words (currently ${wc})`;
      else if (wc > 200) e.motivation = `Please keep to 200 words or fewer (currently ${wc})`;
      if (!form.commitment_agreed) e.commitment_agreed = "You must confirm the commitment";
      if (!form.terms_agreed) e.terms_agreed = "You must agree to continue";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep(s => Math.min(3, s + 1)); };
  const back = () => setStep(s => Math.max(0, s - 1));

  const submit = async () => {
    for (let i = 0; i <= 2; i++) { if (!validate(i)) { setStep(i); return; } }
    setSubmitting(true);
    try {
      const { data } = await api.post("/applications/join", form);
      if (data.waitlisted) {
        navigate("/join/waitinglist", {
          state: { firstName: form.first_name, applicationId: data.id, justSubmitted: true },
          replace: true,
        });
        return;
      }
      setWaitlisted(false);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : (detail || "Please check your entries and try again.");
      toast({ title: "Submission failed", description: msg });
    } finally { setSubmitting(false); }
  };

  const wc = wordCount(form.motivation);

  if (done) {
    return (
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-28 md:py-32 text-center">
        <CheckCircle2 size={56} className="mx-auto mb-6" style={{ color: "var(--gold)" }} />
        <h1 className="hero-title text-[46px] md:text-[72px] leading-[1] mb-6">
          {waitlisted
            ? <>You&rsquo;re <span className="font-serif-i" style={{ color: "var(--orange)" }}>on the list.</span></>
            : <>Application <span className="font-serif-i" style={{ color: "var(--orange)" }}>received.</span></>}
        </h1>
        <p className="text-[17px] max-w-[620px] mx-auto leading-relaxed" style={{ color: "rgba(18,52,40,0.8)" }}>
          {waitlisted
            ? `Thank you, ${form.first_name || 'friend'}. We're currently preparing the network for launch and will begin reviewing applications ahead of opening membership. We'll be in touch when there is an update.`
            : `Thank you, ${form.first_name || 'friend'}. Our team will review your application. If approved, we'll email you a secure link to activate your member account.`}
        </p>
      </section>
    );
  }

  return (
    <div>
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-16 md:pt-20 pb-6">
        <div className="section-eyebrow mb-6" style={{ color: "rgba(18,52,40,0.6)" }}>Diaspora Network · Application</div>
        <h1 className="hero-title text-[46px] md:text-[80px] leading-[0.95]">
          Tell us <span className="font-serif-i" style={{ color: "var(--orange)" }}>who you are.</span>
        </h1>
        <p className="mt-8 max-w-[720px] text-[16px] leading-relaxed" style={{ color: "rgba(18,52,40,0.8)" }}>
          Membership is free. This takes about 6 minutes. We&rsquo;ll match your expertise to an organisation where a small commitment will make a real difference.
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="pt-4 border-t-2" style={{ borderColor: i <= step ? "var(--gold)" : "rgba(18,52,40,0.15)" }}>
              <div className="text-[11px]" style={{ color: "rgba(18,52,40,0.6)" }}>{i + 1}</div>
              <div className="text-[14px] font-medium" style={{ color: "var(--forest)" }}>{s}</div>
            </div>
          ))}
        </div>

        <div className="rounded-lg p-8 md:p-10 editorial-card" style={{ background: "var(--cream)" }}>
          {step === 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="text-[12px] text-[#0d2a20]/70">First name *</label><input value={form.first_name} onChange={e => upd('first_name', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /><FieldError msg={errors.first_name} /></div>
              <div><label className="text-[12px] text-[#0d2a20]/70">Last name *</label><input value={form.last_name} onChange={e => upd('last_name', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /><FieldError msg={errors.last_name} /></div>
              <div><label className="text-[12px] text-[#0d2a20]/70">Email *</label><input value={form.email} onChange={e => upd('email', e.target.value)} type="email" className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /><FieldError msg={errors.email} /></div>
              <div><label className="text-[12px] text-[#0d2a20]/70">Phone</label><input value={form.phone} onChange={e => upd('phone', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
              <div className="md:col-span-2"><label className="text-[12px] text-[#0d2a20]/70">Country of residence *</label><div className="mt-1"><SearchableSelect value={form.country} onChange={v => upd('country', v)} options={options.countries || []} placeholder="Start typing your country…" /></div><FieldError msg={errors.country} /></div>
              <div className="md:col-span-2">
                <label className="text-[12px] text-[#0d2a20]/70">How did you hear about Vemoye? *</label>
                <select value={form.hear} onChange={e => upd('hear', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
                  <option value="">Select an option…</option>
                  {(options.hear_sources || []).map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="Other">Other</option>
                </select>
                <FieldError msg={errors.hear} />
                {form.hear === "Other" && (
                  <div className="mt-2">
                    <label className="text-[12px] text-[#0d2a20]/70">Please specify *</label>
                    <input value={form.hear_other} onChange={e => upd('hear_other', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                    <FieldError msg={errors.hear_other} />
                  </div>
                )}
                {form.hear === "Word of mouth" && (
                  <div className="mt-2" data-testid="hear-referrer-field">
                    <label className="text-[12px] text-[#0d2a20]/70">Who told you about Vemoye?</label>
                    <input
                      value={form.hear_referrer}
                      onChange={e => upd('hear_referrer', e.target.value)}
                      placeholder="Their full name"
                      className="w-full mt-1 px-3 py-2 rounded-md bg-white border"
                      style={{ borderColor: 'rgba(13,42,32,0.15)' }}
                      data-testid="hear-referrer-input"
                    />
                    <div className="text-[11px] text-[#0d2a20]/55 mt-1">
                      If they&rsquo;re already a Vemoye Diaspora Network member, please share their name.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] text-[#0d2a20]/70">Most relevant job title *</label>
                  <input value={form.role} onChange={e => upd('role', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                  <div className="text-[11px] text-[#0d2a20]/55 mt-1">This does not have to be your current role. Please provide the job title most relevant to the expertise you would bring to the Vemoye Network.</div>
                  <FieldError msg={errors.role} />
                </div>
                <div>
                  <label className="text-[12px] text-[#0d2a20]/70">Years of experience *</label>
                  <input inputMode="numeric" pattern="\d*" maxLength={3} value={form.years} onChange={e => upd('years', e.target.value.replace(/[^\d]/g, '').slice(0, 3))} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                  <div className="text-[11px] text-[#0d2a20]/55 mt-1">Whole numbers only, up to 3 digits.</div>
                  <FieldError msg={errors.years} />
                </div>
              </div>
              <div>
                <label className="text-[12px] text-[#0d2a20]/70">Industry *</label>
                <div className="mt-1"><SearchableSelect value={form.industry} onChange={v => upd('industry', v)} options={[...(options.industries || []), "Other"]} placeholder="Start typing your industry…" /></div>
                <FieldError msg={errors.industry} />
                {form.industry === "Other" && (
                  <div className="mt-2">
                    <label className="text-[12px] text-[#0d2a20]/70">Please specify *</label>
                    <input value={form.industry_other} onChange={e => upd('industry_other', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                    <FieldError msg={errors.industry_other} />
                  </div>
                )}
              </div>
              <div>
                <label className="text-[12px] text-[#0d2a20]/70">Areas of expertise * <span className="text-[#0d2a20]/40">(select all that apply)</span></label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[...(options.skills || []), "Other"].map(s => {
                    const sel = form.skills.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleSkill(s)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[13px] transition-colors ${sel ? 'text-white' : 'bg-white border'}`} style={sel ? { background: 'var(--gold)' } : { borderColor: 'rgba(18,52,40,0.15)' }}>
                        {sel && <Check size={12} />} {s}
                      </button>
                    );
                  })}
                </div>
                <FieldError msg={errors.skills} />
                {form.skills.includes("Other") && (
                  <div className="mt-2">
                    <label className="text-[12px] text-[#0d2a20]/70">Please specify *</label>
                    <input value={form.skills_other} onChange={e => upd('skills_other', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                    <FieldError msg={errors.skills_other} />
                  </div>
                )}
              </div>

              {/* Professional profile — strongly encouraged */}
              <div className="pt-6 mt-2 border-t" style={{ borderColor: "rgba(18,52,40,0.12)" }}>
                <div className="section-eyebrow mb-2" style={{ color: "var(--gold-hover)" }}>Professional profile</div>
                <div className="classic-title text-[22px] md:text-[26px] mb-2" style={{ color: "var(--forest)" }}>
                  Help us <span className="font-serif-i" style={{ color: "var(--orange)" }}>verify your expertise</span>
                </div>
                <p className="text-[14px] leading-relaxed mb-5" style={{ color: "rgba(18,52,40,0.8)" }}>
                  As a curated professional network, we want to understand the experience and expertise each member brings. Please share the best source for us to learn more about your professional background and verify the information in your application.
                </p>

                <label className="text-[12px] block mb-2" style={{ color: "rgba(18,52,40,0.7)" }}>
                  How can we learn more about your professional experience?
                </label>
                <div className="grid gap-2" data-testid="profile-kind-options">
                  {PROFILE_OPTIONS.map(opt => (
                    <label
                      key={opt.key}
                      className="flex items-start gap-3 px-4 py-3 rounded-md cursor-pointer transition-colors"
                      style={{
                        background: form.profile_kind === opt.key ? "rgba(205,163,78,0.12)" : "white",
                        border: `1px solid ${form.profile_kind === opt.key ? "var(--gold)" : "rgba(18,52,40,0.15)"}`,
                      }}
                      data-testid={`profile-kind-${opt.key}`}
                    >
                      <input
                        type="radio"
                        name="profile_kind"
                        value={opt.key}
                        checked={form.profile_kind === opt.key}
                        onChange={() => pickProfileKind(opt.key)}
                        className="mt-0.5"
                        style={{ accentColor: "var(--gold)" }}
                      />
                      <span>
                        <span className="text-[14px] block" style={{ color: "rgba(18,52,40,0.9)" }}>{opt.label}</span>
                        {opt.helper && (
                          <span className="text-[12px] block mt-0.5" style={{ color: "rgba(18,52,40,0.55)" }}>{opt.helper}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>

                {form.profile_kind === "linkedin" && (
                  <div className="mt-4" data-testid="profile-linkedin-input">
                    <label className="text-[12px]" style={{ color: "rgba(18,52,40,0.7)" }}>LinkedIn profile URL</label>
                    <input
                      value={form.profile_url}
                      onChange={e => upd('profile_url', e.target.value)}
                      placeholder="https://linkedin.com/in/…"
                      className="w-full mt-1 px-3 py-2 rounded-md bg-white border"
                      style={{ borderColor: 'rgba(18,52,40,0.15)' }}
                    />
                    <FieldError msg={errors.profile_url} />
                  </div>
                )}

                {form.profile_kind === "website" && (
                  <div className="mt-4" data-testid="profile-website-input">
                    <label className="text-[12px]" style={{ color: "rgba(18,52,40,0.7)" }}>Professional website or portfolio URL</label>
                    <input
                      value={form.profile_url}
                      onChange={e => upd('profile_url', e.target.value)}
                      placeholder="https://…"
                      className="w-full mt-1 px-3 py-2 rounded-md bg-white border"
                      style={{ borderColor: 'rgba(18,52,40,0.15)' }}
                    />
                    <FieldError msg={errors.profile_url} />
                  </div>
                )}

                {form.profile_kind === "other" && (
                  <div className="mt-4" data-testid="profile-other-input">
                    <label className="text-[12px]" style={{ color: "rgba(18,52,40,0.7)" }}>URL of the professional webpage</label>
                    <input
                      value={form.profile_url}
                      onChange={e => upd('profile_url', e.target.value)}
                      placeholder="https://…"
                      className="w-full mt-1 px-3 py-2 rounded-md bg-white border"
                      style={{ borderColor: 'rgba(18,52,40,0.15)' }}
                    />
                    <FieldError msg={errors.profile_url} />
                  </div>
                )}

                {form.profile_kind === "cv" && (
                  <div className="mt-4" data-testid="profile-cv-input">
                    <label className="text-[12px]" style={{ color: "rgba(18,52,40,0.7)" }}>Upload your CV</label>
                    {!form.cv_storage_path ? (
                      <label
                        className="mt-1 flex items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed cursor-pointer transition-colors"
                        style={{ borderColor: "rgba(18,52,40,0.25)", background: "white", color: "rgba(18,52,40,0.75)" }}
                        data-testid="cv-upload-dropzone"
                      >
                        {cvUploading ? (
                          <><Loader2 className="animate-spin" size={16} /> <span className="text-[13px]">Uploading…</span></>
                        ) : (
                          <><Upload size={16} /> <span className="text-[13px]">Choose a PDF, DOC or DOCX (max 10 MB)</span></>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept={CV_ACCEPT}
                          disabled={cvUploading}
                          data-testid="cv-upload-input"
                          onChange={e => uploadCv(e.target.files?.[0])}
                        />
                      </label>
                    ) : (
                      <div className="mt-1 flex items-center justify-between gap-3 px-4 py-3 rounded-md" style={{ background: "white", border: "1px solid rgba(18,52,40,0.15)" }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={16} style={{ color: "var(--gold)" }} />
                          <div className="min-w-0">
                            <div className="text-[14px] truncate" style={{ color: "var(--forest)" }} data-testid="cv-filename">{form.cv_filename}</div>
                            <div className="text-[11px]" style={{ color: "rgba(18,52,40,0.55)" }}>Uploaded</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearCv}
                          data-testid="cv-remove-btn"
                          className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded"
                          style={{ color: "rgba(18,52,40,0.7)" }}
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      </div>
                    )}
                    <FieldError msg={errors.cv_upload} />
                  </div>
                )}

                {/* Why we ask */}
                <div
                  className="mt-6 p-4 rounded-md"
                  style={{ background: "rgba(205,163,78,0.08)", border: "1px solid rgba(205,163,78,0.35)" }}
                  data-testid="profile-why-we-ask"
                >
                  <div className="section-eyebrow mb-1" style={{ color: "var(--gold-hover)" }}>Why we ask</div>
                  <p className="text-[13px] leading-relaxed" style={{ color: "rgba(18,52,40,0.85)" }}>
                    Vemoye is built around members contributing their professional expertise. Understanding your experience helps us maintain the quality of the network and identify opportunities where your skills could be most valuable.
                  </p>
                </div>

                {/* Verification privacy note */}
                <p className="mt-3 text-[12px] leading-relaxed" style={{ color: "rgba(18,52,40,0.6)" }}>
                  Vemoye may review the information you provide here and, where reasonably necessary, relevant professional information about you that is publicly available online, to verify your experience and suitability for the Network. See our{" "}
                  <Link to="/privacy" target="_blank" className="link-underline" style={{ color: "var(--gold-hover)" }}>Privacy Policy</Link> for details.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <label className="text-[12px] text-[#0d2a20]/70">What made you want to join our network? *</label>
                <textarea rows={6} value={form.motivation} onChange={e => upd('motivation', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
                <div className="flex justify-between text-[11px] text-[#0d2a20]/55 mt-1">
                  <span>Take a moment to be thoughtful and concise.</span>
                  <span className={wc < 20 || wc > 200 ? "text-[#7a1f13]" : ""}>{wc} / 20–200 words</span>
                </div>
                <FieldError msg={errors.motivation} />
              </div>
              <label className="flex items-start gap-3 text-[14px] cursor-pointer" style={{ color: "rgba(18,52,40,0.85)" }}>
                <input type="checkbox" checked={form.commitment_agreed} onChange={e => upd('commitment_agreed', e.target.checked)} className="w-4 h-4 mt-1" style={{ accentColor: 'var(--gold)' }} />
                <span>I confirm that I can commit at least two hours per week for four weeks, at least once a year, to support a Vemoye organisation or project when a suitable opportunity is available. <span className="block text-[12px] mt-1" style={{ color: "rgba(18,52,40,0.6)" }}>This does not mean a project will automatically be allocated to me. It confirms my willingness and capacity to contribute when an appropriate opportunity arises.</span></span>
              </label>
              <FieldError msg={errors.commitment_agreed} />
              <label className="flex items-start gap-3 text-[14px] cursor-pointer" style={{ color: "rgba(18,52,40,0.85)" }}>
                <input type="checkbox" checked={form.terms_agreed} onChange={e => upd('terms_agreed', e.target.checked)} className="w-4 h-4 mt-1" style={{ accentColor: 'var(--gold)' }} />
                <span>I confirm that I have read and agree to the <Link to="/terms" target="_blank" className="link-underline" style={{ color: "var(--gold-hover)" }}>Terms &amp; Conditions</Link> and acknowledge the <Link to="/privacy" target="_blank" className="link-underline" style={{ color: "var(--gold-hover)" }}>Privacy Policy</Link>.</span>
              </label>
              <FieldError msg={errors.terms_agreed} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2 text-[14px]">
              <div className="section-eyebrow mb-3" style={{ color: "var(--gold-hover)" }}>Review your application</div>
              {[
                ["Name", `${form.first_name} ${form.last_name}`],
                ["Email", form.email], ["Phone", form.phone || '—'], ["Country", form.country],
                ["How did you hear", form.hear === 'Other' ? `Other — ${form.hear_other}` : (form.hear === 'Word of mouth' && form.hear_referrer ? `Word of mouth — ${form.hear_referrer}` : form.hear)],
                ["Job title", form.role], ["Years of experience", form.years],
                ["Industry", form.industry === 'Other' ? `Other — ${form.industry_other}` : form.industry],
                ["Areas of expertise", form.skills.map(s => s === 'Other' ? `Other — ${form.skills_other}` : s).join(', ')],
                ["Professional profile",
                  form.profile_kind === "linkedin" ? `LinkedIn — ${form.profile_url || '—'}` :
                  form.profile_kind === "website"  ? `Website — ${form.profile_url || '—'}` :
                  form.profile_kind === "other"    ? `Other professional webpage — ${form.profile_url || '—'}` :
                  form.profile_kind === "cv"       ? `CV — ${form.cv_filename || 'uploaded'}` :
                  "—"],
                ["Motivation", form.motivation],
                ["Commitment", form.commitment_agreed ? 'Confirmed' : 'Not confirmed'],
                ["Terms & Privacy", form.terms_agreed ? 'Agreed' : 'Not agreed'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2 border-b" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
                  <span className="text-[#0d2a20]/60">{k}</span>
                  <span className="text-right text-[#0d2a20] max-w-[65%] whitespace-pre-wrap break-words">{v || '—'}</span>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-3">
                <button onClick={() => setStep(0)} className="px-3 py-1.5 rounded-md btn-ghost text-[12px]">Edit personal info</button>
                <button onClick={() => setStep(1)} className="px-3 py-1.5 rounded-md btn-ghost text-[12px]">Edit expertise</button>
                <button onClick={() => setStep(2)} className="px-3 py-1.5 rounded-md btn-ghost text-[12px]">Edit motivation</button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-8">
          <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-ghost text-[14px] disabled:opacity-40"><ArrowLeft size={14} /> Back</button>
          {step < 3 ? (
            <button onClick={next} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-forest text-[14px]">Continue <ArrowRight size={14} /></button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="join-submit-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-md btn-gold text-[14px] font-medium disabled:opacity-60">{submitting ? 'Submitting…' : 'Submit Application'} <ArrowRight size={14} /></button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Join;
